import { Box, Grid, Stack } from '@mui/material';
import { useMemo } from 'react';
import VegaText from '../../../../components/common/VegaText';
import { BORDER_COLLECTION, COMMON } from '../../../../constants/style';
import {
  AnalyticsQueryTypeEnum,
  CelenderTypeEmun,
} from '../../../../features/analyticsDashboardSlice';
import {
  AnalyticsQueryParamsProps,
  AnalyticsQueryRequest,
} from '../../../../types/request/analyticsRequest';
import { DateUtility } from '../../../../utils/DateUtlility';
import { GraphTypeEnum, ProfitTypeProps } from '../../AnalyticsDashboard';
import CollectionTeamGraph from '../../components/CollectionTeamGraph';
import RoleBaseSegmentPerformance from '../../components/RoleBaseSegmentPerformance';
import TeleCallersGraph from '../../components/TeleCallersGraph';
import CollectionValueGraph from './modules/CollectionValueGraph';
import CollectionVolumeGraph from './modules/CollectionVolumeGraph';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../../router/routes';

interface IProps extends ProfitTypeProps {}

const TodayContainer = ({ selectedCollectionProfitType }: IProps) => {
  const navigate = useNavigate();
  const TODAY = new Date();
  const request = {
    analyticsQueryType: AnalyticsQueryTypeEnum.BUCKET_PERFORMANCE,
    params: {
      fromDate: DateUtility.formatStringToYYYYMMDD(TODAY?.toISOString()),
      toDate: DateUtility.formatStringToYYYYMMDD(TODAY?.toISOString()),
      isMonthToDate: 'false',
      isYearToDate: 'false',
      type: selectedCollectionProfitType,
    },
  } as AnalyticsQueryRequest;

  const teleCallersGraph = useMemo(() => {
    const updatedParams = {
      ...request.params,
      role: AnalyticsQueryTypeEnum.TELE_COLLECTION_EXECUTIVE,
    } as AnalyticsQueryParamsProps;
    const requestUpdate = {
      ...request,
      params: updatedParams,
    } as AnalyticsQueryRequest;
    return (
      <Box
        sx={{
          transition: 'all 0.3s ease-in-out',
          cursor: 'pointer',
          ':hover': {
            transform: 'scale(1.01)',
          },
        }}
        onDoubleClick={() =>
          navigate(
            ROUTES.ANALYTICS_PARAMS.replace(
              ':viewType',
              GraphTypeEnum.TELE_CALLERS
            )
              .replace(':calenderType', CelenderTypeEmun.TODAY)
              .replace(':profitType', selectedCollectionProfitType)
          )
        }
      >
        <TeleCallersGraph
          request={requestUpdate}
          calenderType={CelenderTypeEmun.TODAY}
          selectedCollectionProfitType={selectedCollectionProfitType}
        />
      </Box>
    );
  }, [selectedCollectionProfitType]);

  const collectionTeamGraph = useMemo(() => {
    const updatedParams = {
      ...request.params,
      role: AnalyticsQueryTypeEnum.CE,
    } as AnalyticsQueryParamsProps;
    const requestUpdate = {
      ...request,
      params: updatedParams,
    } as AnalyticsQueryRequest;
    return (
      <Box
        sx={{
          transition: 'all 0.3s ease-in-out',
          cursor: 'pointer',
          ':hover': {
            transform: 'scale(1.01)',
          },
        }}
        onDoubleClick={() =>
          navigate(
            ROUTES.ANALYTICS_PARAMS.replace(
              ':viewType',
              GraphTypeEnum.COLLECTION_TEAM
            )
              .replace(':calenderType', CelenderTypeEmun.TODAY)
              .replace(':profitType', selectedCollectionProfitType)
          )
        }
      >
        <CollectionTeamGraph
          request={requestUpdate}
          calenderType={CelenderTypeEmun.TODAY}
          selectedCollectionProfitType={selectedCollectionProfitType}
          headerText="Collection Team (in crores)"
        />
      </Box>
    );
  }, [selectedCollectionProfitType]);

  const collectionValue = useMemo(() => {
    return (
      <Box
        sx={{
          transition: 'all 0.3s ease-in-out',
          cursor: 'pointer',
          ':hover': {
            transform: 'scale(1.01)',
          },
        }}
        onDoubleClick={() =>
          navigate(
            ROUTES.ANALYTICS_PARAMS.replace(
              ':viewType',
              GraphTypeEnum.COLLECTION_VALUE
            )
              .replace(':calenderType', CelenderTypeEmun.TODAY)
              .replace(':profitType', selectedCollectionProfitType)
          )
        }
      >
        <CollectionValueGraph
          selectedCollectionProfitType={selectedCollectionProfitType}
        />
      </Box>
    );
  }, [selectedCollectionProfitType]);

  const collectionVolume = useMemo(
    () => (
      <Box
        sx={{
          transition: 'all 0.3s ease-in-out',
          cursor: 'pointer',
          ':hover': {
            transform: 'scale(1.01)',
          },
        }}
        onDoubleClick={() =>
          navigate(
            ROUTES.ANALYTICS_PARAMS.replace(
              ':viewType',
              GraphTypeEnum.COLLECTION_VOLUME
            )
              .replace(':calenderType', CelenderTypeEmun.TODAY)
              .replace(':profitType', selectedCollectionProfitType)
          )
        }
      >
        <CollectionVolumeGraph
          selectedCollectionProfitType={selectedCollectionProfitType}
        />
      </Box>
    ),
    [selectedCollectionProfitType]
  );

  const segmentPerformace = useMemo(() => {
    return (
      <Box
        sx={{
          transition: 'all 0.3s ease-in-out',
          cursor: 'pointer',
        }}
        onDoubleClick={() =>
          navigate(
            ROUTES.ANALYTICS_PARAMS.replace(
              ':viewType',
              GraphTypeEnum.ROLE_BASE
            )
              .replace(':calenderType', CelenderTypeEmun.TODAY)
              .replace(':profitType', selectedCollectionProfitType)
          )
        }
      >
        <RoleBaseSegmentPerformance
          calenderType={CelenderTypeEmun.TODAY}
          selectedCollectionProfitType={selectedCollectionProfitType}
        />
      </Box>
    );
  }, [selectedCollectionProfitType]);

  return (
    <>
      <Stack
        sx={{
          p: 3,
          bgcolor: 'white',
          borderRadius: '12px',
          border: BORDER_COLLECTION,
        }}
        gap={2}
      >
        <VegaText
          text={'Today'}
          fontSize={17}
          fontWeight={600}
          color={COMMON.balck}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Stack gap={1}>
              {collectionValue}
              {collectionVolume}
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={4.5}>
            {collectionTeamGraph}
          </Grid>
          <Grid item xs={12} sm={6} md={4.5}>
            {teleCallersGraph}
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            {segmentPerformace}
          </Grid>
        </Grid>
      </Stack>
    </>
  );
};

export default TodayContainer;
